import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import { isEqual } from 'lodash'
import { JSONToValue, valueToJSON, EditorValue, EditorValueJSON } from '@follow/farte'
import { Patient } from '../../../../model/Patient'
import { useUpdatePatient } from '../../../../hooks/queries/patients'
import { useSubscribeMutation } from '../../../../hooks/utils/mutation'
import { patientsKeys } from '../../../../hooks/queries/patients/patients.keys'
import { MergeVariable } from '../../../../hooks/queries/patients/useMergePatient.query'

export const PATIENT_NOTES_SAVE_DELAY = 1000

const toComparableNote = (note: EditorValueJSON) => valueToJSON(JSONToValue(note))

export const usePatientNotesSync = (patient: Patient) => {
  const { mutateAsync: updatePatient } = useUpdatePatient()
  const [localPatientNotes, setLocalPatientNotes] = useState<EditorValue>(() =>
    JSONToValue(patient.note),
  )

  const isNoteDirty = useRef(false)
  const pendingNoteSaves = useRef(0)
  const lastSyncedNote = useRef(toComparableNote(patient.note))

  const resetLocalPatientNotes = useCallback((note: EditorValueJSON) => {
    lastSyncedNote.current = toComparableNote(note)
    isNoteDirty.current = false
    setLocalPatientNotes(JSONToValue(note))
  }, [])

  useEffect(() => {
    if (isEqual(toComparableNote(patient.note), lastSyncedNote.current)) return
    if (isNoteDirty.current || pendingNoteSaves.current > 0) return
    resetLocalPatientNotes(patient.note)
  }, [patient.note, resetLocalPatientNotes])

  useSubscribeMutation<Patient, MergeVariable>(patientsKeys.merge, (mutatePatient) =>
    resetLocalPatientNotes(mutatePatient.note),
  )

  const handleNotesChange = (value: EditorValue) => {
    if (value.document !== localPatientNotes.document) {
      isNoteDirty.current = true
    }
    setLocalPatientNotes(value)
  }

  useDebounce(
    () => {
      if (!isNoteDirty.current) return
      const note = valueToJSON(localPatientNotes)
      isNoteDirty.current = false
      if (isEqual(note, lastSyncedNote.current)) return
      const previousSyncedNote = lastSyncedNote.current
      lastSyncedNote.current = note
      pendingNoteSaves.current++
      updatePatient({ id: patient.id, payload: { note } })
        .catch(() => {
          isNoteDirty.current = true
          if (lastSyncedNote.current === note) {
            lastSyncedNote.current = previousSyncedNote
          }
        })
        .finally(() => {
          pendingNoteSaves.current--
        })
    },
    PATIENT_NOTES_SAVE_DELAY,
    [localPatientNotes],
  )

  return { localPatientNotes, handleNotesChange }
}
